import React from 'react';
import { Tooltip, Box } from '@mui/material';

interface TruncatedTextWithTooltipProps {
  text: string | null | undefined;
  maxWidth?: number | string;
  maxLines?: number;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
}

/**
 * A component that displays truncated text with a tooltip showing the full text.
 * Useful for table cells with long content.
 *
 * @param text - The text to display
 * @param maxWidth - Maximum width before truncation (default: 200px)
 * @param maxLines - Maximum number of lines before truncation (default: 1)
 * @param placement - Tooltip placement (default: 'top')
 *
 * @example
 * <TruncatedTextWithTooltip text={article.designation} maxWidth={150} />
 * <TruncatedTextWithTooltip text={longDescription} maxLines={2} maxWidth="100%" />
 */
const TruncatedTextWithTooltip: React.FC<TruncatedTextWithTooltipProps> = ({
  text,
  maxWidth = 200,
  maxLines = 1,
  placement = 'top',
}) => {
  const displayText = text || '-';

  const truncateStyles = maxLines === 1
    ? {
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }
    : {
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      };

  return (
    <Tooltip
      title={displayText}
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={100}
    >
      <Box
        component="span"
        sx={{
          ...truncateStyles,
          maxWidth: maxWidth,
          display: maxLines === 1 ? 'block' : '-webkit-box',
          cursor: 'default',
        }}
      >
        {displayText}
      </Box>
    </Tooltip>
  );
};

export default TruncatedTextWithTooltip;
